// components/SkeletonComponents/InvitationSkeleton.jsx
import React from 'react';

export const InvitationSkeleton = () => (
  <div className="">
    {/* Animated Background Blobs */}
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-red-300 to-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-yellow-300 to-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-blue-300 to-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
    </div>

    <div className="relative">
      {/* Header Skeleton */}
      <div className="mb-6 animate-fadeInUp">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg animate-pulse"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg animate-ping opacity-20"></div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Envelope Icon */}
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-400 rounded-xl animate-pulse shadow-lg"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-pink-400 rounded-xl animate-ping opacity-30"></div>
              </div>
              
              <div className="space-y-2">
                {/* Title */}
                <div className="h-8 w-56 bg-gradient-to-r from-slate-300 via-slate-200 to-slate-300 rounded-lg animate-shimmer"></div>
                {/* Subtitle */}
                <div className="h-5 w-80 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded-md animate-shimmer animation-delay-200"></div>
              </div>
            </div>
          </div>
          
          {/* Invite Button */}
          <div className="relative">
            <div className="h-10 w-36 bg-gradient-to-r from-red-400 via-pink-400 to-red-400 rounded-lg animate-pulse shadow-xl"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-red-400 to-pink-400 rounded-lg blur-lg opacity-40 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Stats Cards Skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { gradient: 'from-yellow-100 to-orange-100', iconGradient: 'from-yellow-400 to-orange-400' },
          { gradient: 'from-green-100 to-emerald-100', iconGradient: 'from-green-400 to-emerald-400' },
          { gradient: 'from-red-100 to-pink-100', iconGradient: 'from-red-400 to-pink-400' },
          { gradient: 'from-blue-100 to-indigo-100', iconGradient: 'from-blue-400 to-indigo-400' }
        ].map((card, idx) => (
          <div
            key={idx}
            className={`relative bg-gradient-to-br ${card.gradient} border border-white/50 rounded-xl p-6 overflow-hidden animate-fadeInUp`}
            style={{ animationDelay: `${idx * 100}ms` }}
          >
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            
            <div className="relative flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-3 w-20 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 rounded animate-shimmer"></div>
                <div className="h-8 w-16 bg-gradient-to-r from-gray-400 via-gray-300 to-gray-400 rounded-lg animate-shimmer animation-delay-200"></div>
              </div>
              <div className={`relative w-12 h-12 bg-gradient-to-br ${card.iconGradient} rounded-full animate-pulse shadow-lg`}>
                <div className="absolute inset-0 rounded-full animate-ping opacity-40 bg-white"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 space-y-4 animate-fadeInUp animation-delay-400">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full animate-pulse"></div>
            <div className="w-full h-11 bg-white rounded-lg shadow-sm border border-gray-200 pl-10 overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-transparent via-gray-100 to-transparent animate-shimmer"></div>
            </div>
          </div>
          
          {/* Filter Button */}
          <div className="relative">
            <div className="h-11 w-32 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-red-50 via-red-100 to-red-50 animate-shimmer"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Skeleton */}
      <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-100">
        {/* Table Header */}
        <div className="bg-gradient-to-r from-red-50 via-pink-50 to-red-50 p-4 border-b">
          <div className="grid grid-cols-9 gap-4">
            {['User', 'Contact', 'Role', 'Department', 'Division', 'Status', 'Invited By', 'Dates', 'Actions'].map((_, idx) => (
              <div key={idx} className="relative overflow-hidden">
                <div className="h-4 bg-gradient-to-r from-red-200 via-pink-200 to-red-200 rounded animate-pulse"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer" 
                  style={{ animationDelay: `${idx * 100}ms` }}></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Table Rows */}
        {[...Array(6)].map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="p-4 border-b border-gray-50 hover:bg-gradient-to-r hover:from-gray-50/50 hover:to-transparent transition-all duration-500 animate-fadeInUp"
            style={{ animationDelay: `${(rowIdx + 5) * 100}ms` }}
          >
            <div className="grid grid-cols-9 gap-4 items-center">
              {/* User Cell with Avatar */}
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full animate-pulse bg-gradient-to-br shadow-lg ${
                    ['from-red-400 to-pink-400', 'from-purple-400 to-indigo-400', 'from-blue-400 to-cyan-400', 
                     'from-green-400 to-emerald-400', 'from-yellow-400 to-orange-400', 'from-pink-400 to-rose-400'][rowIdx]
                  }`}></div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 border-2 border-white animate-pulse"></div>
                </div>
                <div className="space-y-1">
                  <div className="h-4 w-28 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 rounded animate-shimmer"></div>
                </div>
              </div>
              
              {/* Contact Cell with Icons */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gradient-to-br from-gray-300 to-gray-400 rounded animate-pulse"></div>
                  <div className="h-3 w-32 bg-gradient-to-r from-blue-100 via-blue-50 to-blue-100 rounded animate-shimmer"></div>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gradient-to-br from-gray-300 to-gray-400 rounded animate-pulse"></div>
                  <div className="h-3 w-24 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-shimmer animation-delay-300"></div>
                </div>
              </div>
              
              {/* Role Badge */}
              <div className="relative">
                <div className={`h-6 w-24 rounded-full shadow-md bg-gradient-to-r ${
                  ['from-purple-200 to-purple-300', 'from-red-200 to-red-300', 'from-blue-200 to-blue-300', 
                   'from-green-200 to-green-300', 'from-yellow-200 to-yellow-300', 'from-pink-200 to-pink-300'][rowIdx]
                } animate-pulse`}></div>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
              </div>
              
              {/* Department */}
              <div className="flex items-center gap-1">
                <div className="w-3.5 h-3.5 bg-gradient-to-br from-purple-300 to-purple-400 rounded animate-pulse"></div>
                <div className="h-4 w-28 bg-gradient-to-r from-purple-100 via-purple-50 to-purple-100 rounded animate-shimmer"></div>
              </div>
              
              {/* Division */}
              <div className="flex items-center gap-1">
                <div className="w-3.5 h-3.5 bg-gradient-to-br from-indigo-300 to-indigo-400 rounded animate-pulse"></div>
                <div className="h-4 w-24 bg-gradient-to-r from-indigo-100 via-indigo-50 to-indigo-100 rounded animate-shimmer animation-delay-200"></div>
              </div>
              
              {/* Status Badge with Dot */}
              <div className="relative">
                <div className={`h-6 w-20 rounded-full shadow-sm bg-gradient-to-r ${
                  rowIdx % 3 === 0 ? 'from-yellow-200 to-yellow-300' :
                  rowIdx % 3 === 1 ? 'from-green-200 to-green-300' : 'from-red-200 to-red-300'
                }`}>
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full animate-pulse bg-white/60"></div>
                </div>
              </div>
              
              {/* Invited By */}
              <div className="flex items-center gap-1">
                <div className="w-3.5 h-3.5 bg-gradient-to-br from-gray-300 to-gray-400 rounded animate-pulse"></div>
                <div className="h-3 w-24 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-shimmer"></div>
              </div>
              
              {/* Dates */}
              <div className="space-y-1">
                <div className="h-3 w-28 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded animate-shimmer"></div>
                <div className="h-2.5 w-24 bg-gradient-to-r from-orange-100 via-orange-50 to-orange-100 rounded animate-shimmer animation-delay-400"></div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-8 h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg animate-pulse shadow-md"></div>
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Skeleton */}
      <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 animate-fadeInUp animation-delay-1000">
        <div className="h-5 w-48 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 rounded animate-shimmer"></div>
        <div className="flex gap-2">
          <div className="relative w-32 h-10 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer"></div>
          </div>
          <div className="flex gap-1">
            {[...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className={`relative w-10 h-10 rounded-lg shadow-sm overflow-hidden ${
                  idx === 2 ? 'bg-gradient-to-r from-red-300 to-pink-300' : 'bg-gradient-to-r from-gray-200 to-gray-300'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer"
                  style={{ animationDelay: `${idx * 200}ms` }}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* CSS Animations */}
    <style >{`
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes blob {
        0%, 100% { transform: translate(0px, 0px) scale(1); }
        33% { transform: translate(50px, -60px) scale(1.2); }
        66% { transform: translate(-30px, 30px) scale(0.8); }
      }
      
      .animate-shimmer {
        animation: shimmer 2.5s linear infinite;
      }
      
      .animate-fadeInUp {
        animation: fadeInUp 0.8s ease-out forwards;
        opacity: 0;
      }
      
      .animate-blob {
        animation: blob 10s infinite;
      }
      
      .animation-delay-200 { animation-delay: 200ms; }
      .animation-delay-300 { animation-delay: 300ms; }
      .animation-delay-400 { animation-delay: 400ms; }
      .animation-delay-600 { animation-delay: 600ms; }
      .animation-delay-800 { animation-delay: 800ms; }
      .animation-delay-1000 { animation-delay: 1000ms; }
      .animation-delay-2000 { animation-delay: 2s; }
      .animation-delay-4000 { animation-delay: 4s; }
    `}</style>
  </div>
);

export default InvitationSkeleton;
